import * as Sentry from '@sentry/astro';
import { isThirdPartyInjectionNoise } from './src/lib/sentry-noise';

if (import.meta.env.PUBLIC_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.PUBLIC_SENTRY_DSN,
    environment: import.meta.env.SENTRY_ENVIRONMENT || 'production',
    tunnel: '/sentry-tunnel', // first-party → satisfies CSP connect-src 'self'
    tracesSampleRate: 0.01,
    replaysSessionSampleRate: 0, // Clarity owns session replay
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    initialScope: { tags: { app: 'seasonal' } },
    beforeSend(event) {
      const exc = event.exception?.values?.[0];
      const msg = exc?.value ?? '';
      const type = exc?.type ?? '';

      // Browser noise: benign errors not worth tracking
      if (/ResizeObserver loop|Non-Error promise rejection|Load failed|NetworkError|Failed to fetch|fetch dynamically imported module|error loading dynamically imported module|skipTransition|Java object is gone|Importing binding name .* is not found|Java bridge method invocation error/.test(msg)) {
        return null;
      }
      // AbortError by type (value varies by browser)
      if (type === 'AbortError') return null;
      // View Transitions API lifecycle — non-fatal, fires when user navigates
      // away mid-transition, taps back/forward rapidly, or (Safari) the
      // viewport resizes mid-transition (URL bar collapse/expand, rotation).
      // Page still works either way.
      if (/Transition was aborted|Transition was skipped|Skipped ViewTransition|Skipping view transition because viewport size changed/.test(msg)) return null;
      // localStorage blocked (Safari private mode, iframe sandboxing, WebViews).
      if (type === 'SecurityError' && /localStorage/.test(msg)) return null;
      // Dynamic import network failure — CDN hiccup or user offline mid-load.
      if (/Importing a module script failed/.test(msg)) return null;
      // Smart-TV/legacy engines, their injected lgcrw_* scripts, and Chrome iOS's
      // injected scripts (see sentry-noise.ts). Engine is read from navigator —
      // event.contexts.browser is only filled in server-side, never here.
      if (isThirdPartyInjectionNoise(event, typeof navigator === 'undefined' ? '' : navigator.userAgent)) return null;
      // Third-party browser injection noise (Quark, VivoBrowser inject scripts
      // that set properties on undefined — obfuscated _0x frames are not our code).
      const frames = exc?.stacktrace?.frames ?? [];
      const hasObfuscatedInjection = frames.some((f: { function?: string }) => /^_0x/.test(f.function ?? ''));
      if (hasObfuscatedInjection) return null;
      // Third-party in-app-browser injection noise — not our code, zero repo references.
      if (/__alook__|WebViewJavascriptBridge is not defined/.test(msg)) return null;
      // UC Browser's own injected shell script (UCShellJava bridge, touch handler)
      // throws on its own internal state — not our code, zero repo references.
      if (/UCShellJava|bodyTouched/.test(msg)) return null;
      // Browser extension's own WebExtension messaging API failing in its
      // background script — this codebase never calls runtime.sendMessage.
      if (/runtime\.sendMessage/.test(msg)) return null;
      // Microsoft Clarity's internal eval usage blocked by our CSP (no unsafe-eval).
      // Non-fatal — Clarity degrades gracefully. CSP intentionally not loosened.
      if (type === 'EvalError' && /unsafe-eval/.test(msg)) return null;
      // Ancient/unusual JS engines choke on modern syntax we ship (no build.target
      // set). Never actionable app-side — engine can't parse ES2022+.
      if (type === 'SyntaxError' && /Unexpected reserved word|Unexpected token/i.test(msg)) return null;
      // Crash entirely inside Microsoft Clarity's own bundle — not our code.
      if (frames.length > 0 && frames.every((f: { filename?: string }) => /\/clarity\.js/.test(f.filename ?? ''))) return null;
      // Astro's own view-transitions script-replay internals (transitions/router.js)
      // hitting a malformed inline <script> during a race — not app code.
      if (frames.some((f: { filename?: string }) => /astro\/dist\/transitions\/router\.js/.test(f.filename ?? ''))) return null;
      // React.lazy()'s own internal accessor (`a._result.default`) throws when a
      // lazy-loaded chunk's promise settles in an unexpected state — not our code,
      // stack is entirely inside react/react-dom/scheduler production bundles.
      if (
        /_result\.default/.test(msg) &&
        frames.length > 0 &&
        frames.every((f: { filename?: string }) => /\/(react|react-dom|scheduler)\/(cjs|umd)\/.*\.production\.min\.js/.test(f.filename ?? ''))
      ) {
        return null;
      }

      if (event.request?.url) event.request.url = event.request.url.split('?')[0];
      return event;
    },
  });
}
